import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useEffect } from 'react'
import { clearRoomTipState } from '../../../../app-state/state.rooms'
import { useRoomTips } from '../../../Hooks/useRoom'
import Coin from '../../../Icons/Coin'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'

const TipSliderRoot = styled('div')({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    zIndex: 24,
    top: getThemeSpacing(2),
    left: getThemeSpacing(2),
    padding: '7px 12px',
    borderRadius: shape.borderRadiusLarge,
    background: 'rgba(0,0,0,0.8)',
    color: palette.commonWhite,
    fontSize: '0.725rem',
    lineHeight: '1.25em',
    cursor: 'pointer',
    '& svg': {
        color: palette.commonWhite,
    },
})

const startingBackgroundColor = 'rgba(190, 156, 0, .8)'
const startingGold = 'rgba(252, 208, 0, .8)'

const glow = keyframes({
    '0%': {
        filter: `drop-shadow(0px 0px 3px ${startingBackgroundColor}) drop-shadow(0px 0px 6px ${startingBackgroundColor})  drop-shadow(0px 0px 10px ${startingBackgroundColor})`,
        fill: startingGold,
        background: startingBackgroundColor,
    },
    '30%': {
        filter: `drop-shadow(0px 0px 2px ${startingBackgroundColor}) drop-shadow(0px 0px 6px ${startingBackgroundColor})`,
        fill: startingGold,
        background: startingGold,
    },
    '80%': {
        filter: `drop-shadow(0px 0px 4px ${startingGold})`,
        fill: startingGold,
    },
    '100%': {
        filter: 'drop-shadow(0px 0px 10px transparent)',
        fill: palette.commonWhite,
    },
})
const fadeOut = keyframes({
    '0%': {
        width: 0,
        opacity: 0,
        marginRight: getThemeSpacing(0.5),
    },
    '5%': {
        width: '100%',
        opacity: 1,
        marginRight: getThemeSpacing(0.5),
    },
    '90%': {
        width: '100%',
        opacity: 1,
        marginRight: getThemeSpacing(0.5),
    },
    '95%': {
        width: '70%',
        opacity: 1,
        marginRight: getThemeSpacing(0.5),
    },
    '100%': {
        width: 0,
        opacity: 0,
        marginRight: 0,
        transformOrigin: 'center',
    },
})
const coinGlowStyles = css({
    width: 'auto',
    animation: `${glow} 3s linear`,
    '& svg': {
        minWidth: '1.25em',
    },
    '& span': {
        animation: `${fadeOut} 8s ease-out`,
        width: 0,
        opacity: 0,
    },
})

interface TipSliderProps {
    id: number
    pushDown?: boolean
    onClick?: () => void
}

const TipSlider = memo((props: TipSliderProps) => {
    const { id, pushDown, onClick } = props
    const tips = useRoomTips(id)
    const top = getThemeSpacing(2, true) + (pushDown ? 40 : 0)
    const tipsExist = tips && tips.length > 0
    const displayTip = tipsExist && !tips[tips.length - 1].displayed
    const amount =
        displayTip && tips[tips.length - 1].amount > 0
            ? tips[tips.length - 1].amount
            : 0
    const tipper = amount > 0 ? tips[tips.length - 1].tipper : ''

    useEffect(() => {
        // eslint-disable-next-line fp/no-let
        let clearTipsTimer: ReturnType<typeof setTimeout>
        if (displayTip) {
            clearTipsTimer = setTimeout(() => {
                clearRoomTipState(id)
            }, 11500)
        }
        return () => {
            clearTimeout(clearTipsTimer)
        }
    }, [tips])

    if (!tipsExist) return

    return (
        <TipSliderRoot
            key={`tip-counter-${id}-${tips?.length}`}
            onClick={onClick}
            data-testid="Chat__Tips"
            title={`View ${tips.length} Recent Tips`}
            css={displayTip ? coinGlowStyles : null}
            style={{ top }}
        >
            {amount > 0 && (
                <span>
                    {amount}&nbsp;{tipper}
                </span>
            )}
            <Coin size={'1.25em'} />
        </TipSliderRoot>
    )
})

export default TipSlider
